import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = { class: "company-editing-drawer__close-trigger" }
const _hoisted_3 = { class: "company-editing-drawer__section" }
const _hoisted_4 = { class: "company-editing-drawer__section--section-content" }
const _hoisted_5 = { class: "company-editing-drawer__logo-row" }
const _hoisted_6 = { class: "company-editing-drawer__logo-preview" }
const _hoisted_7 = ["src"]
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "company-editing-drawer__logo-meta" }
const _hoisted_10 = {
  key: 0,
  class: "company-editing-drawer__logo-actions"
}
const _hoisted_11 = {
  key: 2,
  class: "company-editing-drawer__logo-error"
}
const _hoisted_12 = { class: "company-editing-drawer__section" }
const _hoisted_13 = { class: "company-editing-drawer__section--section-content" }
const _hoisted_14 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { getFileExtension } from '@/js/utils/file-upload-utils';
import { ICONS_BASE, useEditingDrawer } from './composables/use-editing-drawer';

const PROJECT_DESCRIPTION_MAX_LENGTH = 512;
const LOGO_MAX_SIZE_BYTES = 10 * 1024 * 1024;
type CompanyFormResponse = {
  status: 'success' | 'error';
  msg:
    | {
        company_name?: string;
        project_description?: string;
        company_link?: string;
        inn?: string;
        ogrn?: string;
        short_name?: string;
        legal_address?: string;
        actual_address?: string;
        email_company?: string;
        phone_company?: string;
        logo?: string;
      }
    | string;
};

type CompanyFormSnapshot = {
  company_name: string;
  short_name: string;
  project_description: string;
  inn: string;
  ogrn: string;
  legal_address: string;
  actual_address: string;
  email_company: string;
  phone_company: string;
  company_link: string;
  logoServerUrl: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'company-editing-drawer',
  setup(__props) {

const { open, showDrawer, onClose: closeDrawer } = useEditingDrawer();

const drawerFormRef = ref<FormInstance>();
const isSubmitting = ref(false);
const isLoading = ref(false);

const COMPANY_FORM_URL = getApiUrl('/api/form/company/');
const LOGO_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'] as const;
const LOGO_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];

const initialFormState = () => ({
  file: null as File | null,
  file_name: '',
  company_name: '',
  short_name: '',
  project_description: '',
  inn: '',
  ogrn: '',
  legal_address: '',
  actual_address: '',
  email_company: '',
  phone_company: '',
  company_link: '',
});

const form = reactive(initialFormState());
const savedFormState = ref<CompanyFormSnapshot | null>(null);
const logoServerUrl = ref('');
const isLogoDeleted = ref(false);
const logoError = ref('');

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.company_name) &&
    isNotBlankString(form.inn) &&
    isNotBlankString(form.legal_address),
);

function captureFormState(): CompanyFormSnapshot {
  return {
    company_name: form.company_name,
    short_name: form.short_name,
    project_description: form.project_description,
    inn: form.inn,
    ogrn: form.ogrn,
    legal_address: form.legal_address,
    actual_address: form.actual_address,
    email_company: form.email_company,
    phone_company: form.phone_company,
    company_link: form.company_link,
    logoServerUrl: logoServerUrl.value,
  };
}

function hasLogoChanges(initial: CompanyFormSnapshot): boolean {
  return form.file !== null || (isLogoDeleted.value && Boolean(initial.logoServerUrl));
}

function isSameFormState(current: CompanyFormSnapshot, initial: CompanyFormSnapshot): boolean {
  return (
    current.company_name === initial.company_name &&
    current.short_name === initial.short_name &&
    current.project_description === initial.project_description &&
    current.inn === initial.inn &&
    current.ogrn === initial.ogrn &&
    current.legal_address === initial.legal_address &&
    current.actual_address === initial.actual_address &&
    current.email_company === initial.email_company &&
    current.phone_company === initial.phone_company &&
    current.company_link === initial.company_link &&
    current.logoServerUrl === initial.logoServerUrl
  );
}

const hasUnsavedChanges = computed(() => {
  const initial = savedFormState.value;
  if (!initial) return false;

  const current = captureFormState();
  return !isSameFormState(current, initial) || hasLogoChanges(initial);
});

const isEmailValidOrEmpty = computed(
  () => !isNotBlankString(form.email_company) || isValidEmail(form.email_company),
);

const isSaveDisabled = computed(
  () =>
    isLoading.value ||
    isSubmitting.value ||
    !isRequiredFieldsFilled.value ||
    !isEmailValidOrEmpty.value ||
    !hasUnsavedChanges.value ||
    Boolean(logoError.value),
);

const rules: Record<string, Rule[]> = {
  company_name: [{ required: true, message: 'Введите полное название', whitespace: true }],
  short_name: [{ required: false, message: 'Введите сокращенное название', whitespace: true }],
  project_description: [{ required: false, message: 'Введите описание проекта', whitespace: true }],
  inn: [{ required: true, message: 'Введите ИНН', whitespace: true }],
  legal_address: [{ required: true, message: 'Введите юридический адрес', whitespace: true }],
  actual_address: [{ required: false, message: 'Введите фактический адрес', whitespace: true }],
  email_company: [{ required: false, message: 'Введите e-mail', type: 'email' }],
  phone_company: [{ required: false, message: 'Введите телефон', whitespace: true }],
  company_link: [{ required: false, message: 'Введите сайт', whitespace: true }],
};

const logoInputRef = ref<HTMLInputElement | null>(null);
const logoObjectUrl = ref<string>('');

const logoPreviewSrc = computed(() => logoObjectUrl.value || logoServerUrl.value);
const hasLogoPreview = computed(() => Boolean(logoPreviewSrc.value));

function validateLogoFile(file: File): string | null {
  const extension = getFileExtension(file.name);
  const hasValidExtension = LOGO_ALLOWED_EXTENSIONS.includes(
    extension as (typeof LOGO_ALLOWED_EXTENSIONS)[number],
  );
  const hasValidMime = !file.type || LOGO_ALLOWED_MIME_TYPES.includes(file.type);

  if (!hasValidExtension || !hasValidMime) {
    return 'Допустимы только файлы JPG или PNG';
  }

  if (file.size > LOGO_MAX_SIZE_BYTES) {
    return 'Размер файла не должен превышать 10 Мб';
  }

  return null;
}

function clearLogoInput(): void {
  if (logoInputRef.value) {
    logoInputRef.value.value = '';
  }
}

const applyCompanyData = (data: CompanyFormResponse['msg']) => {
  if (!data || typeof data === 'string') return;

  form.company_name = data.company_name ?? '';
  form.short_name = data.short_name ?? '';
  form.project_description = data.project_description ?? '';
  form.inn = data.inn ?? '';
  form.ogrn = data.ogrn ?? '';
  form.legal_address = data.legal_address ?? '';
  form.actual_address = data.actual_address ?? '';
  form.email_company = data.email_company ?? '';
  form.phone_company = data.phone_company ?? '';
  form.company_link = data.company_link ?? '';
  form.file = null;
  form.file_name = '';
  logoServerUrl.value = data.logo ?? '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
};

function setCompanyFieldValue(label: string, value: string): void {
  const companyMount = document.getElementById('company-editing-vue');
  const section = companyMount?.closest<HTMLElement>('.general-information__section');
  if (!section) return;

  section.querySelectorAll<HTMLElement>('.general-information__field').forEach((field) => {
    const labelEl = field.querySelector('.general-information__field-label');
    const valueEl = field.querySelector('.general-information__field-value');

    if (!labelEl || !valueEl) return;
    if (labelEl.textContent?.trim() !== label) return;
    if (valueEl.classList.contains('general-information__field-value--cluster')) return;

    valueEl.textContent = value;
  });
}

function syncCompanyDataToPage(): void {
  const residentName = document.querySelector('[data-resident-name]');
  if (residentName) {
    residentName.textContent = form.company_name;
  }

  const sidebarLogo = document.querySelector<HTMLImageElement>(
    '.residency-lk-sidebar__img_box img',
  );
  if (sidebarLogo) {
    if (logoServerUrl.value) {
      sidebarLogo.src = logoServerUrl.value;
      sidebarLogo.hidden = false;
    } else {
      sidebarLogo.removeAttribute('src');
      sidebarLogo.hidden = true;
    }
  }

  setCompanyFieldValue('Полное наименование', form.company_name);
  setCompanyFieldValue('Сокращенное название', form.short_name);
  setCompanyFieldValue('Описание проекта', form.project_description);
  setCompanyFieldValue('ИНН', form.inn);
  setCompanyFieldValue('ОГРН', form.ogrn);
  setCompanyFieldValue('Юридический адрес', form.legal_address);
  setCompanyFieldValue('Фактический адрес', form.actual_address);
  setCompanyFieldValue('Email', form.email_company);
  setCompanyFieldValue('Телефон', form.phone_company);
  setCompanyFieldValue('Сайт', form.company_link);
}

async function refreshLogoAfterSave(): Promise<void> {
  const response = await fetch(COMPANY_FORM_URL, {
    method: 'GET',
    credentials: 'same-origin',
  });
  const result = (await response.json()) as CompanyFormResponse;

  if (!response.ok || result.status !== 'success' || typeof result.msg === 'string') {
    return;
  }

  logoServerUrl.value = result.msg.logo ?? '';
}

function commitSavedFormState(): void {
  form.file = null;
  form.file_name = '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  savedFormState.value = captureFormState();
}

const openLogoPicker = () => {
  logoInputRef.value?.click();
};

const onLogoChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  logoError.value = '';

  if (!file) {
    return;
  }

  const validationError = validateLogoFile(file);
  if (validationError) {
    logoError.value = validationError;
    showStcErrorNotification(validationError);
    clearLogoInput();
    return;
  }

  form.file = file;
  form.file_name = file.name;
  logoServerUrl.value = '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  logoObjectUrl.value = URL.createObjectURL(file);
};

const removeLogo = () => {
  form.file = null;
  form.file_name = '';
  isLogoDeleted.value = true;
  logoError.value = '';

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  logoServerUrl.value = '';
};

const resetLogoState = () => {
  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  logoServerUrl.value = '';
  isLogoDeleted.value = false;
  logoError.value = '';
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  savedFormState.value = null;
  resetLogoState();

  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const loadCompanyData = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(COMPANY_FORM_URL, {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as CompanyFormResponse;
    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось загрузить данные компании',
      );
    }
    applyCompanyData(result.msg);
    savedFormState.value = captureFormState();
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const openDrawer = async () => {
  showDrawer();
  await loadCompanyData();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) return;

  if (form.file) {
    const validationError = validateLogoFile(form.file);
    if (validationError) {
      logoError.value = validationError;
      showStcErrorNotification(validationError);
      return;
    }
  }

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  isSubmitting.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const body = new FormData();
    body.append('company_name', form.company_name);
    body.append('short_name', form.short_name);
    body.append('project_description', form.project_description);
    body.append('inn', form.inn);
    body.append('ogrn', form.ogrn);
    body.append('legal_address', form.legal_address);
    body.append('actual_address', form.actual_address);
    body.append('email_company', form.email_company);
    body.append('phone_company', form.phone_company);
    body.append('company_link', form.company_link);

    if (form.file) {
      body.append('file', form.file);
    } else if (isLogoDeleted.value) {
      body.append('delete_logo', '1');
    }

    body.append('sessid', sessid);

    const response = await fetch(COMPANY_FORM_URL, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        'X-Bitrix-Csrf-Token': sessid,
      },
    });

    const result = (await response.json()) as CompanyFormResponse;
    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось сохранить данные компании',
      );
    }

    const successMessage = typeof result.msg === 'string' ? result.msg : 'Данные успешно сохранены';

    if (form.file || isLogoDeleted.value) {
      await refreshLogoAfterSave();
    }

    commitSavedFormState();
    syncCompanyDataToPage();
    showStcSuccessNotification(successMessage);
    closeDrawer();
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  closeDrawer();
  resetFormState();
};

onBeforeUnmount(() => {
  resetLogoState();
});

return (_ctx: any,_cache: any) => {
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_tooltip, {
      title: "Редактировать",
      placement: "bottom",
      "overlay-class-name": "general-information-editing-tooltip"
    }, {
      default: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "general-information__company-editing-btn",
          "aria-label": "Редактировать данные компании",
          onClick: openDrawer
        }, [
          _createElementVNode("img", {
            src: `${_unref(ICONS_BASE)}/edit.svg`,
            alt: "",
            class: "general-information__company-editing-icon"
          }, null, 8, _hoisted_1)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_a_drawer, {
      width: 800,
      open: _unref(open),
      class: "company-editing-drawer",
      "body-style": { padding: '0px 40px 32px 40px' },
      "footer-style": { textAlign: 'left', padding: '20px 40px 40px 40px' },
      "header-style": { padding: '32px 40px', border: 'none' },
      onClose: handleClose
    }, {
      title: _withCtx(() => [...(_cache[10] || (_cache[10] = [
        _createElementVNode("span", { class: "company-editing-drawer__title" }, "Данные о компании", -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_component_a_row, {
          align: "middle",
          justify: "end",
          class: "company-editing-drawer__toolbar"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_col, null, {
              default: _withCtx(() => [
                _createVNode(_component_a_space, null, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-outline btn-medium",
                      onClick: handleClose
                    }, " Отмена "),
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-primary btn-medium",
                      disabled: isSaveDisabled.value,
                      onClick: onSubmit
                    }, " Сохранить ", 8, _hoisted_14)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_divider, { class: "company-editing-drawer__divider-header" }),
        _createVNode(_component_a_form, {
          ref_key: "drawerFormRef",
          ref: drawerFormRef,
          class: "company-editing-drawer__form",
          model: form,
          rules: rules,
          layout: "vertical"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "company-editing-drawer__section--section-title" }, "Общее", -1)),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    (logoPreviewSrc.value)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: logoPreviewSrc.value,
                          class: "company-editing-drawer__logo-image"
                        }, null, 8, _hoisted_7))
                      : (_openBlock(), _createElementBlock("img", {
                          key: 1,
                          src: `${_unref(ICONS_BASE)}/preview_img.svg`,
                          class: "company-editing-drawer__logo-icon"
                        }, null, 8, _hoisted_8))
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    (hasLogoPreview.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "btn btn-secondary btn-medium",
                            onClick: openLogoPicker
                          }, " Заменить лого "),
                          _createElementVNode("button", {
                            type: "button",
                            class: "btn btn-outline btn-medium",
                            onClick: removeLogo
                          }, " Удалить ")
                        ]))
                      : (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          type: "button",
                          class: "btn btn-secondary btn-medium",
                          onClick: openLogoPicker
                        }, " Выбрать лого ")),
                    _createElementVNode("input", {
                      ref_key: "logoInputRef",
                      ref: logoInputRef,
                      type: "file",
                      class: "company-editing-drawer__logo-input",
                      accept: ".jpg,.jpeg,.png,image/jpeg,image/png",
                      hidden: "",
                      "aria-hidden": "true",
                      onChange: onLogoChange
                    }, null, 544),
                    _cache[11] || (_cache[11] = _createElementVNode("p", { class: "company-editing-drawer__logo-hint" }, "Файл JPG или PNG до 10МБ", -1)),
                    (logoError.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(logoError.value), 1))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createVNode(_component_a_divider, { class: "company-editing-drawer__field-divider" }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Полное название",
                  name: "company_name",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.company_name,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.company_name) = $event)),
                      placeholder: "Полное название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Сокращенное название",
                  name: "short_name"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.short_name,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.short_name) = $event)),
                      placeholder: "Короткое название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Описание проекта",
                  name: "project_description"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_textarea, {
                      value: form.project_description,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.project_description) = $event)),
                      class: "company-editing-drawer__textarea",
                      placeholder: "Тип проекта, ожидаемые результаты",
                      maxlength: PROJECT_DESCRIPTION_MAX_LENGTH,
                      "auto-size": { minRows: 7, maxRows: 10 },
                      "show-count": ""
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_row, { gutter: 16 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_form_item, {
                          class: "stc-ant-field-skin",
                          label: "ИНН",
                          name: "inn",
                          required: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_input, {
                              value: form.inn,
                              "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.inn) = $event)),
                              placeholder: "0987654321"
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_form_item, {
                          class: "stc-ant-field-skin",
                          label: "ОГРН",
                          name: "ogrn"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_input, {
                              value: form.ogrn,
                              "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.ogrn) = $event)),
                              placeholder: "Номер ОГРН"
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ]),
            _createVNode(_component_a_divider, { class: "company-editing-drawer__divider" }),
            _createElementVNode("div", _hoisted_12, [
              _cache[13] || (_cache[13] = _createElementVNode("span", { class: "company-editing-drawer__section--section-title" }, "Контакты", -1)),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Юридический адрес",
                  name: "legal_address",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.legal_address,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((form.legal_address) = $event)),
                      placeholder: "Адрес"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Фактический адрес",
                  name: "actual_address"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.actual_address,
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((form.actual_address) = $event)),
                      placeholder: "Адрес"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Email",
                  name: "email_company"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.email_company,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((form.email_company) = $event)),
                      placeholder: "company@example.com"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_row, { gutter: 16 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_form_item, {
                          class: "stc-ant-field-skin",
                          label: "Телефон",
                          name: "phone_company"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_input, {
                              value: form.phone_company,
                              "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((form.phone_company) = $event)),
                              placeholder: "+7 (___) ___-__-__"
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_form_item, {
                          class: "stc-ant-field-skin",
                          label: "Сайт",
                          name: "company_link"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_input, {
                              value: form.company_link,
                              "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((form.company_link) = $event)),
                              placeholder: "site.example.com"
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})